import React, { useCallback, useEffect, useMemo, useState } from "react"
import { useAuth0 } from "@auth0/auth0-react"
import { AlertTriangle, Bitcoin, Copy, ExternalLink, Landmark, ReceiptText, ShieldCheck, X } from "lucide-react"
import { parseApiError } from "../../lib/apiErrors"

const formatBtc = (value) => {
  const number = Number(value)
  if (!Number.isFinite(number)) {
    return "0.00000000 BTC"
  }
  return `${number.toFixed(8)} BTC`
}

const formatDate = (value) => {
  if (!value) return "Pending"
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return "Pending"
  return new Intl.DateTimeFormat(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  }).format(date)
}

const shortAddress = (value) => {
  const text = String(value || "").trim()
  if (!text) return "No receive address"
  if (text.length <= 22) return text
  return `${text.slice(0, 10)}...${text.slice(-8)}`
}

const normalizeRows = (rows) => {
  if (!Array.isArray(rows)) return []
  return [...rows].sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0))
}

export default function GroupBtcStatement({ groupId }) {
  const { getAccessTokenSilently, isAuthenticated } = useAuth0()
  const apiUrl = process.env.REACT_APP_API_URL
  const [statement, setStatement] = useState(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState("")
  const [showStatement, setShowStatement] = useState(false)
  const [copied, setCopied] = useState(false)

  const statementErrorForStatus = useCallback(async (response) => {
    if (response.status === 401) {
      return "Sign in again to view this group treasury."
    }
    if (response.status === 403) {
      return "Join this group before viewing its treasury statement."
    }
    if (response.status === 404) {
      return "Group BTC statement is not available yet."
    }
    if (response.status === 503) {
      return (await parseApiError(response, "Group BTC treasury is temporarily unavailable.")).message
    }

    return (await parseApiError(response, "Could not load group BTC statement.")).message
  }, [])

  const fetchStatement = useCallback(async () => {
    if (!groupId || !isAuthenticated || !apiUrl) return

    setLoading(true)
    setError("")

    try {
      const token = await getAccessTokenSilently({
        authorizationParams: {
          audience: process.env.REACT_APP_API_AUDIENCE,
        },
      })
      const response = await fetch(`${apiUrl}/api/Groups/${groupId}/btc-statement?pageSize=100`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })

      if (!response.ok) {
        throw new Error(await statementErrorForStatus(response))
      }

      setStatement(await response.json())
    } catch (err) {
      console.warn("Group BTC statement unavailable:", err)
      setStatement(null)
      setError(err.message || "Could not load group BTC statement.")
    } finally {
      setLoading(false)
    }
  }, [apiUrl, getAccessTokenSilently, groupId, isAuthenticated, statementErrorForStatus])

  useEffect(() => {
    fetchStatement()
  }, [fetchStatement])

  const rows = useMemo(() => normalizeRows(statement?.rows), [statement])
  const hasDepositIndexingGap = statement?.depositHistoryStatus === "ExternalDepositsRequireOnChainIndexing"

  const copyAddress = async () => {
    if (!statement?.walletAddress) return
    try {
      await navigator.clipboard.writeText(statement.walletAddress)
      setCopied(true)
      window.setTimeout(() => setCopied(false), 1800)
    } catch {
      setCopied(false)
    }
  }

  if (!isAuthenticated) {
    return null
  }

  return (
    <>
      <section className="group-btc-widget" aria-label="Group Bitcoin statement">
        <div className="group-btc-widget-header">
          <span className="group-section-kicker"><Bitcoin className="h-4 w-4" /> Group treasury</span>
          <button
            type="button"
            className="group-btc-link"
            onClick={() => setShowStatement(true)}
            disabled={!statement?.hasWallet}
          >
            View statement <ExternalLink className="h-4 w-4" />
          </button>
        </div>

        {loading ? (
          <div className="group-btc-loading">
            <div className="group-task-spinner" />
            <span>Loading BTC statement...</span>
          </div>
        ) : error ? (
          <div className="group-btc-unavailable">
            <AlertTriangle className="h-5 w-5" />
            <span>{error}</span>
          </div>
        ) : statement?.hasWallet ? (
          <>
            <div className="group-btc-balance-row">
              <div>
                <p>Current balance</p>
                <strong>{formatBtc(statement.currentBalance)}</strong>
              </div>
              <span className="group-btc-status">{statement.balanceStatus || "Live"}</span>
            </div>

            <div className="group-btc-address">
              <span>{shortAddress(statement.walletAddress)}</span>
              <button type="button" onClick={copyAddress} aria-label="Copy group BTC receive address">
                <Copy className="h-4 w-4" /> {copied ? "Copied" : "Copy"}
              </button>
            </div>

            <div className="group-btc-totals">
              <span><strong>{formatBtc(statement.confirmedIncomingAmount)}</strong> confirmed in</span>
              <span><strong>{formatBtc(statement.pendingIncomingAmount)}</strong> pending in</span>
              <span><strong>{formatBtc(statement.confirmedOutgoingAmount)}</strong> confirmed out</span>
              <span><strong>{formatBtc(statement.pendingOutgoingAmount)}</strong> pending out</span>
              <span><strong>{formatBtc(statement.failedOutgoingAmount)}</strong> failed</span>
            </div>
          </>
        ) : (
          <>
            <div className="group-btc-unavailable">
              <Landmark className="h-5 w-5" />
              <span>No group treasury wallet has been provisioned by the system yet. Deposits and governed payouts will appear here after the treasury is ready.</span>
            </div>
          </>
        )}
      </section>

      {showStatement && statement?.hasWallet && (
        <div className="group-statement-backdrop" role="dialog" aria-modal="true" aria-label="Group BTC statement">
          <div className="group-statement-modal">
            <header className="group-statement-header">
              <div>
                <span className="group-section-kicker"><ReceiptText className="h-4 w-4" /> Bank-style statement</span>
                <h2>Group BTC Statement</h2>
                <p>Generated {formatDate(statement.generatedAt)}</p>
              </div>
              <button type="button" className="group-statement-close" onClick={() => setShowStatement(false)} aria-label="Close group BTC statement">
                <X className="h-5 w-5" />
              </button>
            </header>

            <div className="group-statement-summary">
              <div>
                <span>Balance</span>
                <strong>{formatBtc(statement.currentBalance)}</strong>
              </div>
              <div>
                <span>Receive address</span>
                <strong title={statement.walletAddress}>{shortAddress(statement.walletAddress)}</strong>
              </div>
              <div>
                <span>Rows</span>
                <strong>{rows.length}</strong>
              </div>
            </div>

            {hasDepositIndexingGap && (
              <div className="group-statement-note">
                <ShieldCheck className="h-5 w-5" />
                <p>
                  The current balance is live from Bitcoin Core. Individual external donation and deposit rows require on-chain indexing and may not be fully listed yet.
                </p>
              </div>
            )}

            <div className="group-statement-table-wrap">
              <table className="group-statement-table">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Type</th>
                    <th>Description</th>
                    <th>Status</th>
                    <th>Amount</th>
                    <th>Txid</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.length === 0 ? (
                    <tr>
                      <td colSpan="6">No BTC statement rows have been recorded yet.</td>
                    </tr>
                  ) : (
                    rows.map((row) => (
                      <tr key={`${row.paymentIntentId || row.transactionId || row.createdAt}-${row.createdAt}-${row.entryType}`}>
                        <td>{formatDate(row.createdAt)}</td>
                        <td>{row.entryType || row.direction || "BTC"}</td>
                        <td>{row.description || "Group BTC movement"}</td>
                        <td>
                          <span className="group-btc-row-status">{row.status || "Unknown"}</span>
                          {Number.isFinite(Number(row.confirmationCount)) && (
                            <small>{row.confirmationCount}/{row.requiredConfirmations || 0} confirmations</small>
                          )}
                        </td>
                        <td>{formatBtc(row.amount)}</td>
                        <td title={row.transactionId}>{shortAddress(row.transactionId)}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </>
  )
}
